import {
  API_HTTP_ERROR_PREFIX,
  HTTP_STATUS,
  TASK_LOOKUP_MESSAGES,
  TASK_PLATFORM_LABELS,
} from '../constants';
import { tasksService } from '../services/tasks.service';
import { AsanaTask, TaskPlatform, TaskSearchResult } from '../types';

export interface TaskSearchOption {
  id: string;
  label: string;
  subtitle?: string;
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : '';

/** Stable dropdown id for a search result; gids are only unique per platform. */
export const getTaskSearchOptionId = (result: TaskSearchResult): string =>
  `${result.source}:${result.gid}`;

export const toTaskSearchOption = (result: TaskSearchResult): TaskSearchOption => ({
  id: getTaskSearchOptionId(result),
  label: result.name,
  subtitle: [TASK_PLATFORM_LABELS[result.source], result.status, result.client]
    .filter(Boolean)
    .join(' · '),
});

/**
 * True when apiService failed with exactly this HTTP status ("HTTP Error: <status> <text>").
 * A prefix match, so a status-like number elsewhere in the message (e.g. the search term
 * inside a timeout error's URL) is not mistaken for the status.
 */
const isHttpStatusError = (error: unknown, status: string): boolean =>
  getErrorMessage(error).startsWith(`${API_HTTP_ERROR_PREFIX} ${status}`);

export const getTaskSearchErrorMessage = (error: unknown): string =>
  isHttpStatusError(error, HTTP_STATUS.SERVICE_UNAVAILABLE)
    ? TASK_LOOKUP_MESSAGES.SEARCH_UNAVAILABLE
    : TASK_LOOKUP_MESSAGES.SEARCH_FAILED;

export const getTaskFetchErrorMessage = (
  error: unknown,
  platform: TaskPlatform,
  taskId: string,
): string => {
  const message = getErrorMessage(error);
  const platformLabel = TASK_PLATFORM_LABELS[platform];

  if (message.includes(HTTP_STATUS.NOT_FOUND) || message.includes('not found')) {
    return `Task "${taskId}" not found`;
  }
  if (message.includes(HTTP_STATUS.UNAUTHORIZED) || message.includes(HTTP_STATUS.FORBIDDEN)) {
    return `${platformLabel} API authentication failed`;
  }
  // The backend reports upstream failures, including a rejected API key, as 502
  if (isHttpStatusError(error, HTTP_STATUS.BAD_GATEWAY)) {
    return `${platformLabel} request failed. Try again, or contact support if it keeps happening.`;
  }
  return `Failed to fetch task from ${platformLabel}`;
};

const fetchTaskByPlatform = (platform: TaskPlatform, taskId: string): Promise<AsanaTask> =>
  platform === 'asana'
    ? tasksService.getAsanaTaskById(taskId)
    : tasksService.getClickUpTaskById(taskId);

/**
 * Fetches the full task from Asana or ClickUp and hands it to `onTaskSelect`.
 * Shared by the name search and the fetch-by-ID paths so both select the same task shape.
 * @returns null on success, otherwise a user-facing error message (onTaskSelect is not called)
 */
export const fetchAndSelectTask = async (
  platform: TaskPlatform,
  taskId: string,
  onTaskSelect: (task: AsanaTask) => void,
): Promise<string | null> => {
  try {
    const task = await fetchTaskByPlatform(platform, taskId);
    if (!task?.gid) {
      return TASK_LOOKUP_MESSAGES.INVALID_TASK;
    }
    onTaskSelect(task);
    return null;
  } catch (error) {
    console.error(`${TASK_PLATFORM_LABELS[platform]} task fetch error:`, error);
    return getTaskFetchErrorMessage(error, platform, taskId);
  }
};
